<template lang="pug">
    div.portfolio
        div.portfolioHeader

            // Title
            title-component(type="h3" title="Portfolio Config")

            div.portfolioControls

                // Frequency update
                frequency-update-component(
                    :frequency="refreshTime"
                    :refreshAction="frequencyCallback"
                )

        t-t-component(v-if="!secretKey")
        div.marketsContent(v-else)

            // Fields selection
            v-autocomplete(
                v-model="selectedFields"
                :items="selectFieldsList"
                outlined
                chips
                small-chips
                close
                label="Fields"
                multiple
            )

            // Fields Drag and Drop
            draggable-component(
                :items="selectedFields"
                :callBackFunction="draggableCallbackFields"
                :dictionary="$t('portfolio.fieldsDictionary')"
            )

            div(v-if="positionsListDictionary && Object.keys(positionsListDictionary).length")

                v-divider.my-8

                // Positions selection
                v-autocomplete(
                    v-model="selectedPositions"
                    :items="positionsList"
                    outlined
                    chips
                    small-chips
                    close
                    label="Positions"
                    multiple
                )

                // Positions Drag and Drop
                draggable-component(
                    :items="selectedPositions"
                    :callBackFunction="draggableCallback"
                    :dictionary="positionsListDictionary"
                )

            v-divider.my-5

            div.text-right

                // Submit button
                v-btn.mr-0.submit(
                    type="submit"
                    color="primary"
                    large
                    @click="submit"
                    :isLoading="isLoading"
                ) {{ $t('fields.save') }}
</template>

<script>
    import {
        FrequencyUpdate,
        Title,
        DragAndDrop,
        Separator,
    } from '~components';
    import { mapGetters, mapActions } from 'vuex';
    import { portfolio } from '~api';
    import { Handler } from '~services';
    import TTConfig from './TTConfig';

    const DEFAULT_FIELDS = ['name', 'lastTradeDate', 'pnl', 'netPosition'];

    export default {
        components: {
            DraggableComponent: DragAndDrop,
            TTComponent: TTConfig,
            FrequencyUpdateComponent: FrequencyUpdate,
            TitleComponent: Title,
            SeparatorComponent: Separator,
        },
        data() {
            return {
                selectedPositions: [],
                selectedFields: [],
                availableFields: [
                    'netPosition',
                    'pnlPrice',
                    'pnl',
                    'avgBuy',
                    'name',
                    'alias',
                    'lastTradeDate',
                ],
                isLoading: false,
                newPositionParams: [
                    {
                        text: 'Top',
                        value: 'top',
                    },
                    {
                        text: 'Bottom',
                        value: 'bottom',
                    },
                    {
                        text: 'None',
                        value: 'none',
                    },
                ],
            };
        },
        computed: {
            ...mapGetters('portfolio', ['refreshTime']),
            ...mapGetters('tt', [
                'secretKey',
                'positionsList',
                'positionsListDictionary',
            ]),

            selectFieldsList() {
                return this.availableFields.map((value) => ({
                    text: this.$t('portfolio.fieldsDictionary')[value].name,
                    value,
                }));
            },
        },
        async created() {
            this.getSavedPositions();
            this.getRefreshTime();
            this.getList();

            const [respPortfolioFields, respPortfolioWatchList] =
                await Promise.all([
                    portfolio.getPortfolioFields(),
                    portfolio.getPortfolioWatchlist(),
                ]);

            if (respPortfolioFields.ok)
                this.selectedFields =
                    respPortfolioFields.data || DEFAULT_FIELDS;

            if (respPortfolioWatchList.ok)
                this.selectedPositions = respPortfolioWatchList.data;
        },
        methods: {
            ...mapActions('tt', ['getList', 'getSavedPositions']),
            ...mapActions('portfolio', ['getRefreshTime', 'setRefreshTime']),

            /**
             * Set refresh time
             * @param e
             */
            frequencyCallback(e) {
                this.setRefreshTime(e);
            },

            /**
             * Set sorted positions from draggble
             * @param sortedItems
             */
            draggableCallback(sortedItems) {
                this.selectedPositions = sortedItems;
            },

            /**
             * Set sorted fields from draggble
             * @param sortedItems
             */
            draggableCallbackFields(sortedItems) {
                this.selectedFields = sortedItems;
            },

            async submit() {
                await this.updateFields();
                this.updatePositions();
            },

            async updatePositions() {
                const successMessage = this.$t(
                    'portfolio.updateWatchlistSuccessMessage'
                );
                this.isLoading = true;

                const resp = await portfolio.updatePortfolioWatchList(
                    this.selectedPositions
                );

                Handler.handleResponse(resp, successMessage);

                this.isLoading = false;
            },

            async updateFields() {
                const successMessage = this.$t(
                    'portfolio.updateFieldsSuccessMessage'
                );
                this.isLoading = true;

                const resp = await portfolio.updatePortfolioFields(
                    this.selectedFields
                );

                Handler.handleResponse(resp, successMessage);

                this.isLoading = false;
            },
        },
    };
</script>

<style lang="stylus" scoped>
    @import "../../../../assets/style/_variables.styl"

    .portfolioHeader
        display: flex
        align-items: center
        justify-content: space-between

    .portfolioControls
        display: flex

    .selectPosition
        margin-right: 15px

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .portfolioHeader
            flex-direction: column
            align-items: flex-start
            justify-content:  flex-start

        .selectPosition
            margin-right: 0

        .portfolioControls
            flex-direction: column
            width: 100%

            &>
                width: 100%
</style>
